import React, { useState, useEffect } from "react";
import { useToast } from "./ui/toast";
import { livestreamAPI } from "../services/api";
import { useAuthStore } from "../stores";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import {
	ExternalLink,
	Loader2,
	Copy,
} from "lucide-react";

interface LiveStream {
	id: string;
	title: string;
	description?: string;
	type: "video" | "audio" | "video_audio";
	status: "created" | "live" | "ended";
}

interface PublicStreamLinksDialogProps {
	stream: LiveStream | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	description?: string;
}

const PublicStreamLinksDialog: React.FC<PublicStreamLinksDialogProps> = ({
	stream,
	open,
	onOpenChange
}) => {
	const [publicToken, setPublicToken] = useState<string>("");
	const [loadingToken, setLoadingToken] = useState(false);
	const [tokenError, setTokenError] = useState<string | null>(null);
	const [autoConnect, setAutoConnect] = useState(true); // Default to auto-connect enabled

	const { addToast } = useToast();
	const user = useAuthStore((state) => state.user);

	useEffect(() => {
		if (open && stream) {
			// Automatically generate/fetch token when dialog opens
			generatePublicTokenAutomatically();
		}
	}, [open, stream]);

	const generatePublicTokenAutomatically = async () => {
		if (!stream || !user) {
			setTokenError("Stream or user information not available.");
			return;
		}

		setLoadingToken(true);
		setTokenError(null);
		setPublicToken(""); // Reset token
		
		try {
			// Generate app-free token using the authenticated endpoint
			// The backend will return existing valid token or create new one
			const response = await livestreamAPI.generateAppFreeToken({
				livestream_id: stream.id,
				permissions: {
					can_publish: true,
					can_view: true,
				},
				expires_in_hours: 24,
			});

			setPublicToken(response.data.data.token);
		} catch (error) {
			console.error("Failed to generate token:", error);
			setTokenError("Failed to generate public token. Please try again.");
		} finally {
			setLoadingToken(false);
		}
	};

	const copyToClipboard = (text: string, label: string) => {
		navigator.clipboard.writeText(text);
		addToast({
			title: "Link Copied",
			description: `${label} has been copied to clipboard.`,
			variant: "success",
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[700px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<ExternalLink className="h-5 w-5" />
						Live Stream Links
					</DialogTitle>
					<DialogDescription>
						Links that can be safely shared outside the dashboard without requiring app credentials.
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
				

					{/* Loading or Error State */}
					{loadingToken && (
						<div className="flex items-center justify-center py-4">
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							<span className="text-sm text-muted-foreground">Generating public links...</span>
						</div>
					)}

					{tokenError && (
						<div className="bg-destructive/10  rounded-lg p-3">
							<p className="text-sm text-destructive">{tokenError}</p>
							<Button
								onClick={generatePublicTokenAutomatically}
								variant="outline"
								size="sm"
								className="mt-2"
							>
								Try Again
							</Button>
						</div>
					)}

					{/* Public URLs Display */}
					{publicToken && stream && (
						<div className="grid gap-3 pt-2">
							<div className="flex items-center justify-between py-2 border-b">
								<div className="space-y-0.5">
									<Label className="text-base">Auto-connect</Label>
									<p className="text-sm text-muted-foreground">
										Automatically connect to the stream when the page loads
									</p>
								</div>
								<Switch
									checked={autoConnect}
									onCheckedChange={setAutoConnect}
								/>
							</div>
							<div className="grid gap-2">
								<Label>Publisher URL</Label>
								<div className="flex gap-2">
									<div className="flex-1">
										<Input
											readOnly
											value={`${window.location.origin}/public/stream/${stream.id}/publish?token=${publicToken}${autoConnect ? '' : '&auto_connect=0'}` || ""}
										/>
									</div>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												size="icon"
												variant="outline"
												onClick={() =>
													copyToClipboard(
														`${window.location.origin}/public/stream/${stream.id}/publish?token=${publicToken}${autoConnect ? '' : '&auto_connect=0'}` || "",
														"Public Publisher URL",
													)
												}
											>
												<Copy className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											Copy Publisher URL
										</TooltipContent>
									</Tooltip>
								</div>
							</div>
							<div className="grid gap-2">
								<Label>Viewer URL</Label>
								<div className="flex gap-2">
									<div className="flex-1">
										<Input
											readOnly
											value={`${window.location.origin}/public/stream/${stream.id}/view${autoConnect ? '' : '?auto_connect=0'}` || ""}
										/>
									</div>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												size="icon"
												variant="outline"
												onClick={() =>
													copyToClipboard(
														`${window.location.origin}/public/stream/${stream.id}/view${autoConnect ? '' : '?auto_connect=0'}` || "",
														"Public Viewer URL",
													)
												}
											>
												<Copy className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											Copy Viewer URL
										</TooltipContent>
									</Tooltip>
								</div>
							</div>
						</div>
					)}
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
					>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default PublicStreamLinksDialog;